import type { RecordingCaptureMetadata } from "./recording-db"

export type AudioSignal = "signal" | "silent" | "unavailable"
export type CaptureWarning = "stalled" | "silent" | "unavailable" | null

export class RecordingHealth {
  private lastChunkAt: number
  private lastSampleAt: number
  private silentSince: number | null = null
  private unavailableSince: number | null = null
  private paused = false
  private durationMs = 0
  private monitoredMs = 0
  private silentMs = 0
  private unverifiedMs = 0
  private signalDetected = false
  private issues = new Set<string>()

  constructor(now: number) {
    this.lastChunkAt = now
    this.lastSampleAt = now
  }

  chunk(now: number) {
    this.lastChunkAt = now
  }

  pause(now: number) {
    this.advance(now)
    this.paused = true
    this.silentSince = null
    this.unavailableSince = null
  }

  resume(now: number) {
    this.lastSampleAt = now
    this.lastChunkAt = now
    this.paused = false
  }

  interrupt(reason: string) {
    this.issues.add(reason)
  }

  sample(now: number, signal: AudioSignal): CaptureWarning {
    const elapsed = this.advance(now)
    if (this.paused) return null

    if (signal === "unavailable") {
      this.unverifiedMs += elapsed
      this.unavailableSince ??= now
      this.silentSince = null
    } else {
      this.unavailableSince = null
      // A frozen tab cannot monitor the microphone during the gap.
      this.monitoredMs += Math.min(elapsed, 1000)
      if (signal === "signal") {
        this.signalDetected = true
        this.silentSince = null
      } else {
        this.silentMs += Math.min(elapsed, 1000)
        this.silentSince ??= now
      }
    }

    if (now - this.lastChunkAt > 20_000) {
      this.issues.add("capture_stalled")
      return "stalled"
    }
    if (this.unavailableSince !== null && now - this.unavailableSince >= 3000) return "unavailable"
    if (this.silentSince !== null && now - this.silentSince >= 12_000) return "silent"
    return null
  }

  metadata(now: number): RecordingCaptureMetadata {
    this.advance(now)
    return {
      ended_at: new Date(now).toISOString(),
      duration_ms: this.durationMs,
      signal_detected: this.signalDetected,
      monitored_ms: this.monitoredMs,
      silent_ms: this.silentMs,
      unverified_ms: this.unverifiedMs,
      issues: [...this.issues],
    }
  }

  private advance(now: number) {
    const elapsed = Math.max(0, now - this.lastSampleAt)
    if (!this.paused) this.durationMs += elapsed
    this.lastSampleAt = now
    return elapsed
  }
}
